import { useEffect, useMemo, useRef } from 'react'

import { FEATHER_API_URL } from '@src/configs/env'
import { ResultCode, StepNames } from '@src/enums'
import { FeatherBackendStatus, IFeatherOrderStatusResponse } from '@src/types/feather'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { useConfigStore } from '@src/zustand_stores'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('FeatherWebSocket')

// Define WebSocket message types
interface WebSocketMessage {
    type: string // Message type (e.g., "order_update", "ping", "error")
    data: any // Message data, content depends on Type
    time?: string // Timestamp in ISO 8601 format
}

interface PingMessage {
    status: string // Always "ping"
    time: string // Current time in ISO 8601 format
}

interface ErrorMessage {
    error: string
    details?: string
    code?: number
}

/**
 * Hook to subscribe to Feather order updates via WebSocket
 * @param exchangeId - The exchange ID to track in the history store
 * @param orderId - The Feather order ID to subscribe to
 */
export const useFeatherOrderUpdates = (exchangeId: string, orderId: string) => {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const getOrder = useHistoryStore(state => state.getOrder)
    const transactionData = getOrder(exchangeId)

    // Add a ref to track the active connection
    const wsRef = useRef<WebSocket | null>(null)
    // Also track the current order ID to prevent reconnecting to the same order
    const currentOrderIdRef = useRef<string | null>(null)
    const reconnectAttemptsRef = useRef(0)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const MAX_RECONNECT_ATTEMPTS = 3

    // Prepare WebSocket URL
    const wssBaseUrl = useMemo(() => {
        return FEATHER_API_URL?.replace('https', 'wss')
    }, [])

    const wssUrlEndpoint = useMemo(() => {
        return wssBaseUrl && orderId ? `${wssBaseUrl}/v1/orders/status/${orderId}` : null
    }, [wssBaseUrl, orderId])

    // Helper function to map backend status to frontend step
    const mapBackendStatusToStep = (status: string): { step: StepNames; resultStatus: ResultCode } => {
        // Mapping object for status -> step/result
        const statusMap: Record<string, { step: StepNames; resultStatus: ResultCode }> = {
            // Waiting stages
            [FeatherBackendStatus.UNKNOWN]: {
                step: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                resultStatus: ResultCode.pending,
            },
            [FeatherBackendStatus.ORDER_RECEIVED]: {
                step: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                resultStatus: ResultCode.pending,
            },
            [FeatherBackendStatus.PROCESSING]: {
                step: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                resultStatus: ResultCode.pending,
            },
            [FeatherBackendStatus.AWAITING_DEPOSIT]: {
                step: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                resultStatus: ResultCode.pending,
            },

            // Processing stages
            [FeatherBackendStatus.DEPOSIT_CONFIRMED]: {
                step: StepNames.FEATHER_DEPOSIT_DETECTED,
                resultStatus: ResultCode.pending,
            },
            [FeatherBackendStatus.TRADING]: {
                step: StepNames.FEATHER_PROCESSING,
                resultStatus: ResultCode.pending,
            },
            [FeatherBackendStatus.TRADED]: {
                step: StepNames.FEATHER_PROCESSING,
                resultStatus: ResultCode.pending,
            },
            [FeatherBackendStatus.WITHDRAWING]: {
                step: StepNames.FEATHER_SENDING_TO_DESTINATION,
                resultStatus: ResultCode.pending,
            },
            [FeatherBackendStatus.WITHDRAWAL_PENDING]: {
                step: StepNames.FEATHER_SENDING_TO_DESTINATION,
                resultStatus: ResultCode.pending,
            },

            // Success case
            [FeatherBackendStatus.COMPLETED]: {
                step: StepNames.FEATHER_COMPLETED,
                resultStatus: ResultCode.success,
            },

            // Failure cases
            [FeatherBackendStatus.DEPOSIT_TIMEOUT]: {
                step: StepNames.FEATHER_EXPIRED,
                resultStatus: ResultCode.failure,
            },
            [FeatherBackendStatus.TRADE_FAILED]: {
                step: StepNames.FEATHER_FAILED,
                resultStatus: ResultCode.failure,
            },
            [FeatherBackendStatus.WITHDRAWAL_FAILED]: {
                step: StepNames.FEATHER_FAILED,
                resultStatus: ResultCode.failure,
            },
            [FeatherBackendStatus.FAILED]: {
                step: StepNames.FEATHER_FAILED,
                resultStatus: ResultCode.failure,
            },
        }

        // Get the mapping or use a default if status is not recognized
        const result = statusMap[status]

        if (!result) {
            logger.warn(`Unknown Feather status received: ${status}, defaulting to WAITING_FOR_DEPOSIT`)
            return {
                step: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                resultStatus: ResultCode.pending,
            }
        }

        return result
    }

    useEffect(() => {
        // Skip if we already have an active connection to this order
        if (orderId && currentOrderIdRef.current === orderId && wsRef.current) {
            logger.info('Skipping WebSocket connection - already connected to this order', {
                orderId,
                currentOrderId: currentOrderIdRef.current,
            })
            return
        }

        // Update the current order ID ref
        currentOrderIdRef.current = orderId

        // Close any existing connection before creating a new one
        if (wsRef.current) {
            logger.info('Closing existing WebSocket connection', {
                previousOrderId: currentOrderIdRef.current,
                newOrderId: orderId,
            })
            wsRef.current.close()
            wsRef.current = null
        }

        logger.info('Starting Feather order update subscription', {
            orderId,
            wssUrlEndpoint,
            exchangeId,
            transactionData,
        })

        // Exit if we're in the final state
        if (
            !transactionData ||
            transactionData.currentStep === StepNames.FEATHER_COMPLETED ||
            transactionData.currentStep === StepNames.FEATHER_FAILED
        ) {
            logger.info('Skipping WebSocket connection - already in final state', {
                currentStep: transactionData?.currentStep,
            })
            return
        }

        if (!orderId || !wssUrlEndpoint) {
            logger.info('Skipping WebSocket connection - missing orderId or endpoint', {
                orderId,
                wssUrlEndpoint,
            })
            return
        }

        // Log at the console level too for easier debugging
        logger.info('📡 Connecting to Feather WebSocket:', wssUrlEndpoint)
        logger.info('📡 Order ID:', orderId)
        logger.info('📡 Exchange ID:', exchangeId)

        logger.info('Connecting to Feather WebSocket', { wssUrlEndpoint })

        const connectWebSocket = (url: string) => {
            // Close existing connection if any
            if (wsRef.current) {
                logger.info(`[FeatherOrderUpdates] Closing existing WebSocket`)
                wsRef.current.close()
                wsRef.current = null
            }

            // Create new WebSocket connection
            logger.info(`[FeatherOrderUpdates] Creating new WebSocket: ${url}`)
            const ws = new WebSocket(url)
            wsRef.current = ws

            ws.onopen = () => {
                logger.info(`[FeatherOrderUpdates] WebSocket connected for ${orderId}`)
                // Reset reconnect attempts on successful connection
                reconnectAttemptsRef.current = 0
            }

            ws.onmessage = event => {
                try {
                    // Parse the received message and handle the wrapper structure
                    const message: WebSocketMessage = JSON.parse(event.data as string)
                    logger.info('Received WebSocket message', message)

                    // Handle the message based on its type
                    switch (message.type) {
                        case 'order_update':
                            handleOrderUpdate(message.data)
                            break
                        case 'ping':
                            // No need to do anything for ping messages
                            logger.debug('Received ping message', message.time)
                            break
                        case 'error':
                            handleErrorMessage(message.data)
                            break
                        default:
                            logger.warn('Received unknown message type', message.type)
                    }
                } catch (error) {
                    console.error('📡 Error processing WebSocket message:', error)
                    logger.error('Error processing Feather WebSocket message:', { error })
                }
            }

            // Function to handle order updates
            const handleOrderUpdate = (data: IFeatherOrderStatusResponse) => {
                // Skip if it's a ping message
                if (data.status === FeatherBackendStatus.PING) {
                    return
                }

                logger.info('Processing order update', { orderId: data.orderId, status: data.status })

                // Get source and destination chain information
                // We're using transaction data if available, otherwise try to determine from available networks
                const sourceChain = transactionData?.sellData?.chainId
                    ? transactionData.sellData.chainId.toString()
                    : Object.keys(availableNetworks)[0]

                const destinationChain = transactionData?.buyData?.chainId
                    ? transactionData.buyData.chainId.toString()
                    : Object.keys(availableNetworks)[0]

                // Create transaction URLs for the explorer using the direct fields
                const sourceTxHash = data.src_deposit_tx
                    ? `${getBlockExplorerUrlFromChainId(sourceChain)}/${data.src_deposit_tx}`
                    : undefined

                const destinationTxHash = data.dst_fill_tx
                    ? `${getBlockExplorerUrlFromChainId(destinationChain)}/${data.dst_fill_tx}`
                    : undefined

                // Map the backend status to our frontend step
                const { step: currentStep, resultStatus } = mapBackendStatusToStep(data.status)

                // Estimated time of arrival in seconds - use timeout directly
                const transactionETA = data.timeout || undefined

                // Get the base message for this status
                let statusMessage = getMessageForStatus(data.status)

                // Directly include the transaction hash in the message for withdrawal-related statuses
                if (data.dst_fill_tx) {
                    if (data.status === FeatherBackendStatus.WITHDRAWING) {
                        statusMessage = `Sending funds to your destination wallet.`
                    } else if (data.status === FeatherBackendStatus.WITHDRAWAL_PENDING) {
                        statusMessage = `Your transaction has been submitted and is awaiting network confirmation.`
                    } else if (data.status === FeatherBackendStatus.COMPLETED) {
                        statusMessage = `Your order has been completed successfully! Funds should be in your wallet.`
                    }
                }

                // Update the history store with the latest status
                const updateData = {
                    currentStep,
                    result: {
                        sourceTxHash,
                        destinationTxHash,
                        status: resultStatus,
                        message: statusMessage,
                        transactionETA,
                    },
                }

                // Only update state if the WebSocket is still open
                if (wsRef.current?.readyState === WebSocket.OPEN) {
                    logger.info(`Updating transaction state for order: ${data.orderId}`, updateData)
                    updateHistory(exchangeId, updateData)
                } else {
                    logger.warn(`WebSocket closed or closing, skipping update for order: ${data.orderId}`)
                }
            }

            // Function to handle error messages
            const handleErrorMessage = (errorData: ErrorMessage) => {
                logger.error('Received error from Feather WebSocket', errorData)

                // Update history with the error
                updateHistory(exchangeId, {
                    currentStep: StepNames.FEATHER_FAILED,
                    result: {
                        status: ResultCode.failure,
                        message: `Error from Feather service: ${errorData.error}${errorData.details ? ` - ${errorData.details}` : ''}. Please contact support.`,
                    },
                })
            }

            // Function to attempt reconnect with exponential backoff
            const attemptReconnect = () => {
                reconnectAttemptsRef.current += 1
                const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000) // Exponential backoff, max 10 seconds

                logger.info(
                    `[FeatherOrderUpdates] Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current} of ${MAX_RECONNECT_ATTEMPTS})`
                )

                reconnectTimeoutRef.current = setTimeout(() => {
                    // Clear the timeout reference
                    reconnectTimeoutRef.current = null

                    // Only reconnect if we haven't mounted a different WebSocket
                    if (currentOrderIdRef.current === orderId) {
                        connectWebSocket(wssUrlEndpoint)
                    }
                }, delay)
            }

            ws.onerror = error => {
                console.error(`[FeatherOrderUpdates] WebSocket error for order ${orderId}:`, error)

                // Attempt to reconnect if we haven't exceeded the maximum number of attempts
                if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                    attemptReconnect()
                } else {
                    // Only update history with failure if we've exceeded the maximum number of reconnect attempts
                    updateHistory(exchangeId, {
                        currentStep: StepNames.FEATHER_FAILED,
                        result: {
                            status: ResultCode.failure,
                            message: `Connection to Feather failed. Please contact support on Discord and provide your Order ID.`,
                        },
                    })
                }
            }

            ws.onclose = event => {
                logger.info(`[FeatherOrderUpdates] WebSocket closed for order ${orderId}:`, event)

                // Clear the reference when connection is closed
                if (wsRef.current === ws) {
                    wsRef.current = null
                }

                // Only attempt to reconnect if the close was not clean and we haven't exceeded the maximum number of attempts
                if (!event.wasClean && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                    attemptReconnect()
                } else if (!event.wasClean) {
                    // Only update history with failure if the close was not clean and we've exceeded the maximum number of reconnect attempts
                    updateHistory(exchangeId, {
                        currentStep: StepNames.FEATHER_FAILED,
                        result: {
                            status: ResultCode.failure,
                            message: `Connection to Feather unexpectedly closed. Please contact support on Discord and provide your Order ID.`,
                        },
                    })
                }
            }
        }

        // Call the connect function
        connectWebSocket(wssUrlEndpoint)

        // Clean up WebSocket connection when component unmounts or dependencies change
        return () => {
            logger.info('Cleaning up Feather WebSocket connection')
            const currentWs = wsRef.current
            if (
                currentWs &&
                (currentWs.readyState === WebSocket.OPEN || currentWs.readyState === WebSocket.CONNECTING)
            ) {
                currentWs.close()
                // Also clear the reference
                wsRef.current = null
            }

            // Clear any pending reconnect timeouts
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current)
                reconnectTimeoutRef.current = null
            }

            // Clear the orderIdRef
            currentOrderIdRef.current = null
        }
    }, [orderId]) // Reduced dependency array to prevent excessive reconnections
}

/**
 * Get a user-friendly message for status
 */
function getMessageForStatus(status: string): string {
    const statusMessages: Record<string, string> = {
        [FeatherBackendStatus.UNKNOWN]: 'Status unknown. Please wait while we check your order...',
        [FeatherBackendStatus.ORDER_RECEIVED]: 'Your order has been received. Please send your deposit to continue.',
        [FeatherBackendStatus.PROCESSING]: 'Your order is being processed. Please send your deposit to continue.',
        [FeatherBackendStatus.AWAITING_DEPOSIT]: 'Waiting for your deposit to be detected on the blockchain...',
        [FeatherBackendStatus.DEPOSIT_TIMEOUT]:
            'Your deposit was not received within the time limit. Please contact our support with your order ID.',
        [FeatherBackendStatus.DEPOSIT_CONFIRMED]: 'Your deposit has been confirmed! We are preparing your exchange...',
        [FeatherBackendStatus.TRADING]: 'Your exchange is in progress. This may take a few minutes...',
        [FeatherBackendStatus.TRADE_FAILED]:
            'There was an issue processing your exchange. Please contact our support with your order ID.',
        [FeatherBackendStatus.TRADED]: 'Exchange completed successfully! Now preparing to send funds to your wallet...',
        [FeatherBackendStatus.WITHDRAWING]: 'Sending funds to your destination wallet.',
        [FeatherBackendStatus.WITHDRAWAL_PENDING]:
            'Your transaction has been submitted and is awaiting confirmation on the blockchain.',
        [FeatherBackendStatus.WITHDRAWAL_FAILED]:
            'We were unable to send funds to your wallet. Please contact our support with your order ID.',
        [FeatherBackendStatus.COMPLETED]: 'Your order has been completed successfully! Funds should be in your wallet.',
        [FeatherBackendStatus.FAILED]: 'Your order has failed. Please contact our support with your order ID.',
    }

    return statusMessages[status] || 'Processing your order. Please wait...'
}
